import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import { themeColors } from '../../styles';

export type CatalogEmptyVariant = 'empty' | 'search' | 'favorites';

interface Props {
  variant: CatalogEmptyVariant;
  /** Shown only for the 'search' variant — clears the active search/filter. */
  onClear?: () => void;
}

const COPY: Record<CatalogEmptyVariant, { title: string; subtitle: string }> = {
  // Catalog genuinely has nothing to show (no products came back).
  empty: {
    title: 'עדיין אין מוצרים בקטלוג',
    subtitle: 'המוצרים יופיעו כאן ברגע שיתווספו. אפשר לנסות לרענן את העמוד בעוד מספר דקות.',
  },
  // A search term or category filter matched nothing.
  search: {
    title: 'לא נמצאו מוצרים',
    subtitle: 'לא מצאנו מוצרים שתואמים את החיפוש. נסו מילה אחרת או נקו את הסינון.',
  },
  // Favorites view with no saved products.
  favorites: {
    title: 'אין עדיין מוצרים מועדפים',
    subtitle: 'לחצו על הלב שליד מוצר כדי לשמור אותו כאן לגישה מהירה.',
  },
};

const ICONS: Record<CatalogEmptyVariant, React.ReactNode> = {
  empty: <Inventory2OutlinedIcon sx={{ fontSize: 46, color: themeColors.primary }} />,
  search: <SearchOffOutlinedIcon sx={{ fontSize: 46, color: themeColors.primary }} />,
  favorites: <FavoriteBorderOutlinedIcon sx={{ fontSize: 46, color: themeColors.attention }} />,
};

/**
 * Shown in place of the catalog grid when there is nothing to render, so the
 * page never reads as a broken blank screen. Three flavours: an empty catalog,
 * a search/filter that matched nothing, and an empty favourites view.
 */
const CatalogEmpty = ({ variant, onClear }: Props) => {
  const { title, subtitle } = COPY[variant];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 8,
        mt: 1,
      }}
    >
      <Box
        sx={{
          width: 104,
          height: 104,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: themeColors.newNotificationBase,
          mb: 2.5,
        }}
      >
        {ICONS[variant]}
      </Box>

      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 700,
          color: themeColors.title,
          mb: 1,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: '21px',
          color: themeColors.paragraphInputTag,
          maxWidth: 300,
        }}
      >
        {subtitle}
      </Typography>

      {variant === 'search' && onClear && (
        <Button
          onClick={onClear}
          variant="outlined"
          sx={{
            mt: 3,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            color: themeColors.primary,
            borderColor: themeColors.primary,
          }}
        >
          ניקוי חיפוש וסינון
        </Button>
      )}
    </Box>
  );
};

export default CatalogEmpty;
